import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import {
  Button,
  Empty,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ARTIFACT_TYPE_LABEL,
  ARTIFACT_TYPE_VISUAL,
  deleteArtifact,
  formatFileSize,
  listArtifacts,
  triggerArtifactDownload,
  truncateError,
} from '@/api/artifacts';
import type { GeneratedArtifact } from '@/api/artifacts';
import { useAuth } from '@/hooks/useAuth';

/**
 * 申报案产物列表（v4 M3-ρ-4）。
 *
 * 嵌入位置：CasesPage 抽屉/卡片底部 — 单 case 维度展示已生成的全部产物。
 *
 * 列：类型 chip、模板包、生成时间、hash 前 10 字符、文件大小、操作（下载/删除）
 * 权限：
 *   - 下载按钮：所有可见角色（artifact:read 已在路由级守卫）
 *   - 删除按钮：仅 PLATFORM（platform_operator）；Modal.confirm 二次确认（ρ-3 Test Writer 教训）
 *
 * 不变量：
 *   - totalCount 用 query.data?.total 而非 items.length（ρ-3 R-high#1）
 *   - 0 hex 硬编码
 *   - 不展示 file_path（后端已隐藏，schema 也无此字段）
 *   - Dropdown 删除走 Modal.confirm 不绕过（ρ-3 教训）
 */
export interface ArtifactsListPanelProps {
  /** 申报案 ID；null 时整面板空 */
  caseId: string | null;
  /** 是否激活拉取（如折叠面板未展开时 enabled=false 省请求） */
  enabled?: boolean;
}

export function ArtifactsListPanel({
  caseId,
  enabled = true,
}: ArtifactsListPanelProps) {
  const { role } = useAuth();
  const qc = useQueryClient();
  const canDelete = role === 'platform_operator';

  const query = useQuery({
    queryKey: ['artifacts', caseId],
    queryFn: () => {
      if (!caseId) throw new Error('caseId missing');
      return listArtifacts(caseId);
    },
    enabled: enabled && !!caseId,
    staleTime: 30_000,
  });

  // 下载/删除底层走 _silent —— 这里 onError 独占弹错，且走 truncateError 防栈泄漏 & 路径泄漏
  const downloadMut = useMutation({
    mutationFn: (artifactId: string) => triggerArtifactDownload(artifactId),
    onError: (err) =>
      message.error(truncateError((err as Error)?.message ?? '下载失败')),
  });

  const deleteMut = useMutation({
    mutationFn: (artifactId: string) => deleteArtifact(artifactId),
    onSuccess: () => {
      message.success('已删除产物');
      qc.invalidateQueries({ queryKey: ['artifacts', caseId] });
    },
    onError: (err) =>
      message.error(truncateError((err as Error)?.message ?? '删除失败')),
  });

  const confirmDelete = (art: GeneratedArtifact) => {
    Modal.confirm({
      title: '删除已生成产物？',
      content: (
        <Space direction="vertical" size={4}>
          <span>
            类型：
            <Tag color="default">{ARTIFACT_TYPE_LABEL[art.artifact_type]}</Tag>
          </span>
          <span style={{ color: 'var(--color-text-tertiary)' }}>
            hash {art.file_hash.slice(0, 16)}…
          </span>
          <span style={{ color: 'var(--color-danger-2)' }}>
            系统会留下完整的删除审计记录，但物理文件无法恢复。
          </span>
        </Space>
      ),
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => deleteMut.mutateAsync(art.id),
    });
  };

  // ρ-3 R-high#1：totalCount 走后端 total，不用 items.length
  const totalCount = query.data?.total ?? 0;
  const items = query.data?.items ?? [];

  const columns: ColumnsType<GeneratedArtifact> = useMemo(
    () => [
      {
        title: '类型',
        dataIndex: 'artifact_type',
        key: 'artifact_type',
        width: 120,
        render: (t: GeneratedArtifact['artifact_type']) => {
          const visual = ARTIFACT_TYPE_VISUAL[t];
          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: visual.bg,
                color: visual.fg,
                border: `1px solid ${visual.border}`,
                borderRadius: 'var(--radius-pill)',
                padding: '0 8px',
                fontSize: 'var(--font-size-xs)',
                lineHeight: '20px',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 'var(--radius-full)',
                  background: visual.fg,
                }}
              />
              {ARTIFACT_TYPE_LABEL[t]}
            </span>
          );
        },
      },
      {
        title: '模板包',
        dataIndex: 'template_pack_id',
        key: 'template_pack_id',
        ellipsis: true,
        render: (v: string) => (
          <Tooltip title={v}>
            <span style={monoStyle}>{v}</span>
          </Tooltip>
        ),
      },
      {
        title: '生成时间',
        dataIndex: 'generated_at',
        key: 'generated_at',
        width: 160,
        render: (v: string) => (
          <span style={monoStyle}>{v?.replace('T', ' ').slice(0, 19)}</span>
        ),
      },
      {
        title: 'hash',
        dataIndex: 'file_hash',
        key: 'file_hash',
        width: 130,
        render: (v: string) => (
          <Tooltip title={v}>
            <span style={monoStyle}>{v.slice(0, 10)}…</span>
          </Tooltip>
        ),
      },
      {
        title: '大小',
        dataIndex: 'file_size',
        key: 'file_size',
        width: 90,
        align: 'right',
        render: (v: number | null) => (
          <span style={monoStyle}>{formatFileSize(v)}</span>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: canDelete ? 150 : 90,
        render: (_: unknown, art: GeneratedArtifact) => (
          <Space size={4}>
            <Button
              size="small"
              type="link"
              loading={
                downloadMut.isPending && downloadMut.variables === art.id
              }
              onClick={() => downloadMut.mutate(art.id)}
            >
              下载
            </Button>
            {canDelete && (
              <Button
                size="small"
                type="link"
                danger
                loading={deleteMut.isPending && deleteMut.variables === art.id}
                onClick={() => confirmDelete(art)}
              >
                删除
              </Button>
            )}
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canDelete, downloadMut.isPending, downloadMut.variables, deleteMut.isPending, deleteMut.variables],
  );

  if (!caseId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="请选择申报案"
      />
    );
  }

  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Text
          type="secondary"
          style={{ fontSize: 'var(--font-size-sm)' }}
        >
          已生成产物 共{' '}
          <strong
            style={{
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {totalCount}
          </strong>{' '}
          个
        </Typography.Text>
        <Button
          size="small"
          onClick={() => query.refetch()}
          loading={query.isFetching}
        >
          刷新
        </Button>
      </div>
      {query.isLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <Table<GeneratedArtifact>
          rowKey="id"
          dataSource={items}
          columns={columns}
          pagination={false}
          size="small"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无已生成产物"
              />
            ),
          }}
        />
      )}
    </Space>
  );
}

const monoStyle: CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};
